import type { IconButtonProps } from '@mui/material/IconButton';

import { useState, useCallback, useMemo } from 'react';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Avatar from '@mui/material/Avatar';
import Popover from '@mui/material/Popover';
import Divider from '@mui/material/Divider';
import MenuList from '@mui/material/MenuList';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import MenuItem, { menuItemClasses } from '@mui/material/MenuItem';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { usePathname, useRouter } from '@src/hooks';
import { Profile } from '@src/utils/state-profile';
import { useTranslation } from 'react-i18next';
import Iconify from '../layout/admin/components/iconify';
import Swal from 'sweetalert2';
import { removeCookie } from '@src/utils/remove-cookie';
import { config } from '@src/config';
import { googleLogout } from '@react-oauth/google';

export type AccountPopoverProps = IconButtonProps & {
    data?: {
        label: string;
        href: string;
        icon?: React.ReactNode;
        info?: React.ReactNode;
    }[];
};

export function AccountPopover({ data = [], sx, ...other }: AccountPopoverProps) {
    const { t } = useTranslation();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));

    const profile = Profile();
    const router = useRouter();
    const pathname = usePathname();

    const [openPopover, setOpenPopover] = useState<HTMLButtonElement | null>(null);

    // Memoize account data to prevent unnecessary re-renders
    const myAccount = useMemo(() => ({
        photoURL: profile?.image || '',
        displayName: profile?.firstName || '',
        email: profile?.email || '',
        fullName: `${profile?.firstName || ''} ${profile?.lastName || ''}`.trim(),
    }), [profile?.firstName, profile?.lastName, profile?.email, profile?.image]);

    // Memoize responsive values
    const responsiveConfig = useMemo(() => ({
        avatar: {
            size: isMobile ? 32 : 40,
            fontSize: isMobile ? '0.875rem' : '1rem',
        },
        typography: {
            variant: isTablet ? 'body2' : 'subtitle1',
            maxWidth: isTablet ? 120 : 200,
        },
        popover: {
            width: isMobile ? 'calc(100vw - 32px)' : isTablet ? 180 : 200,
            maxWidth: isMobile ? 280 : undefined,
            margin: isMobile ? 2 : 0,
        },
        spacing: {
            padding: isMobile ? 1.5 : 2,
            paddingBottom: isMobile ? 1 : 1.5,
            menuPadding: isMobile ? 0.25 : 0.5,
            itemPadding: {
                x: isMobile ? 0.75 : 1,
                y: isMobile ? 0.75 : 1,
            },
            gap: isMobile ? 1.5 : 2,
            minHeight: isMobile ? 40 : 48,
        },
        text: {
            nameVariant: isMobile ? 'body1' : 'subtitle2',
            emailVariant: isMobile ? 'caption' : 'body2',
            emailFontSize: isMobile ? '0.75rem' : '0.875rem',
            menuFontSize: isMobile ? '0.875rem' : '1rem',
            nameWidth: isMobile ? 'calc(100% - 16px)' : '11rem',
            fontWeight: isMobile ? 500 : 600,
        },
        button: {
            size: isMobile ? 'small' : 'medium',
        },
    }), [isMobile, isTablet]);

    const handleOpenPopover = useCallback((event: React.MouseEvent<HTMLButtonElement | HTMLDivElement>) => {
        setOpenPopover(event.currentTarget as HTMLButtonElement);
    }, []);

    const handleClosePopover = useCallback(() => {
        setOpenPopover(null);
    }, []);

    const handleClickItem = useCallback(
        (path: string) => {
            handleClosePopover();
            router.push(path);
        },
        [router, handleClosePopover],
    );

    const onLogout = useCallback(() => {
        Swal.fire({
            html: `<b>${(t as any)('SETTING_PAGE.ALERT_POPUP.LOGOUT_POPUP.TITLE')}</b><p>${(t as any)('SETTING_PAGE.ALERT_POPUP.LOGOUT_POPUP.TEXT')}</p>`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            cancelButtonText: (t as any)('SETTING_PAGE.ALERT_POPUP.LOGOUT_POPUP.CANCEL_BTN'),
            confirmButtonText: (t as any)('SETTING_PAGE.ALERT_POPUP.LOGOUT_POPUP.CONFIRM_BTN'),
            width: '20em',
        }).then(result => {
            if (result.isConfirmed) {
                removeCookie(config.keyNameCookie);
                if (profile?.authType === 'google') {
                    googleLogout();
                }
                window.location.href = '/login';
            } else {
                handleClosePopover();
            }
        });
    }, [t, profile?.authType, handleClosePopover]);

    return (
        <>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    cursor: 'pointer',
                    maxWidth: { xs: '150px', sm: 'none' },
                    overflow: 'hidden',
                }}
                onClick={handleOpenPopover}
            >
                <IconButton
                    onClick={handleOpenPopover}
                    sx={{
                        p: '2px',
                        width: responsiveConfig.avatar.size,
                        height: responsiveConfig.avatar.size,
                        ...sx,
                    }}
                    {...other}
                >
                    <Avatar
                        src={myAccount.photoURL}
                        alt={myAccount.displayName}
                        sx={{
                            width: 1,
                            height: 1,
                            fontSize: responsiveConfig.avatar.fontSize,
                        }}
                    >
                        {myAccount.displayName.charAt(0).toUpperCase()}
                    </Avatar>
                </IconButton>

                <Typography
                    variant={responsiveConfig.typography.variant as any}
                    sx={{
                        display: { xs: 'none', sm: 'block' },
                        fontWeight: 400,
                        fontFamily: 'kanit',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        maxWidth: responsiveConfig.typography.maxWidth,
                    }}
                >
                    {myAccount.displayName}
                </Typography>
            </Box>

            <Popover
                open={!!openPopover}
                anchorEl={openPopover}
                onClose={handleClosePopover}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: isMobile ? 'left' : 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: isMobile ? 'left' : 'right',
                }}
                slotProps={{
                    paper: {
                        sx: {
                            width: responsiveConfig.popover.width,
                            maxWidth: responsiveConfig.popover.maxWidth,
                            mx: responsiveConfig.popover.margin,
                        },
                    },
                }}
            >
                {/* User Info Section */}
                <Box
                    sx={{
                        p: responsiveConfig.spacing.padding,
                        pb: responsiveConfig.spacing.paddingBottom
                    }}
                >
                    <Typography
                        variant={responsiveConfig.text.nameVariant as any}
                        noWrap
                        sx={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            width: responsiveConfig.text.nameWidth,
                            fontWeight: responsiveConfig.text.fontWeight,
                        }}
                    >
                        {myAccount.fullName}
                    </Typography>

                    <Typography
                        variant={responsiveConfig.text.emailVariant as any}
                        sx={{
                            color: 'text.secondary',
                            fontSize: responsiveConfig.text.emailFontSize,
                        }}
                        noWrap
                    >
                        {myAccount.email}
                    </Typography>
                </Box>

                {/* Menu Items */}
                <MenuList
                    disablePadding
                    sx={{
                        p: 0,
                        gap: responsiveConfig.spacing.menuPadding,
                        display: 'flex',
                        flexDirection: 'column',
                        [`& .${menuItemClasses.root}`]: {
                            px: responsiveConfig.spacing.itemPadding.x,
                            py: responsiveConfig.spacing.itemPadding.y,
                            gap: responsiveConfig.spacing.gap,
                            borderRadius: 0.75,
                            color: 'text.secondary',
                            fontSize: responsiveConfig.text.menuFontSize,
                            minHeight: responsiveConfig.spacing.minHeight,
                            transition: theme.transitions.create(['color', 'background-color'], {
                                duration: theme.transitions.duration.short,
                            }),
                            '&:hover': {
                                color: 'text.primary',
                                bgcolor: 'action.hover',
                            },
                            [`&.${menuItemClasses.selected}`]: {
                                color: 'text.primary',
                                bgcolor: 'action.selected',
                                fontWeight: 'fontWeightSemiBold',
                            },
                        },
                    }}
                >
                    {data.map(option => (
                        <MenuItem
                            key={option.label}
                            selected={option.href === pathname}
                            onClick={() => handleClickItem(option.href)}
                        >
                            {option.icon}
                            {option.label}
                            {option.info}
                        </MenuItem>
                    ))}
                </MenuList>

                {/* Logout Section */}
                <Box sx={{ p: responsiveConfig.spacing.menuPadding }}>
                    <Divider sx={{ borderStyle: 'dashed', mb: responsiveConfig.spacing.menuPadding }} />

                    <Button
                        fullWidth
                        onClick={onLogout}
                        color="error"
                        size={responsiveConfig.button.size as any}
                        variant="text"
                        startIcon={<Iconify icon="carbon:logout" />}
                        sx={{
                            py: responsiveConfig.spacing.itemPadding.y,
                            fontSize: responsiveConfig.text.menuFontSize,
                            minHeight: responsiveConfig.spacing.minHeight,
                            fontFamily: 'Kanit',
                            textTransform: 'capitalize',
                            transition: theme.transitions.create(['background-color'], {
                                duration: theme.transitions.duration.short,
                            }),
                            '&:hover': {
                                bgcolor: 'error.alpha8',
                            },
                        }}
                    >
                        {t('SETTING_PAGE.LOGOUT')}
                    </Button>
                </Box>
            </Popover>
        </>
    );
}